import { Typography } from "@suraasa/placebo-ui"
import { MasterclassTabs } from "features/Masterclasses"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { useEffect, useState } from "react"
import Joyride, { Step } from "react-joyride"
import { NavigateFunction } from "react-router-dom"
import { BROWSER_STORAGE_KEYS } from "utils/constants"

import { SpnyTourEnum, useSuraasaPlusNewYear } from "../Context"
import { tourManager } from "../TourManager"
import { useTourContext } from "../TourProvider"
import FinalStep from "./FinalStep"
import OnboardingStep from "./OnboardingStep"
import ResumeMasterclassTour from "./ResumeMasterclassTour"
import { MASTERCLASS_ONBOARDING_TARGETS } from "./types"

type Props = {
  onStart: () => void
  onEnd: () => void
  setActiveTab: (tab: MasterclassTabs) => void
  navigate?: NavigateFunction
  hasUpcomingMasterclass: boolean
  isLoading: boolean
}

const withUpcomingMasterclasses: Step[] = [
  {
    target: `[data-tour="${MASTERCLASS_ONBOARDING_TARGETS.explore}"]`,
    content: (
      <>
        <Typography variant="title3" className="mb-1">
          Explore Masterclasses
        </Typography>
        <Typography variant="smallBody" className="text-onSurface-600">
          Explore 60+ expert masterclass recordings and tune in for upcoming
          ones.
        </Typography>
      </>
    ),
    disableBeacon: true,
  },

  {
    target: `[data-tour="${MASTERCLASS_ONBOARDING_TARGETS.upcoming}"]`,
    content: (
      <>
        <Typography variant="title3" className="mb-1">
          Upcoming Masterclasses
        </Typography>
        <Typography variant="smallBody" className="text-onSurface-600">
          Find all the upcoming masterclass sessions here.
        </Typography>
      </>
    ),
    disableBeacon: true,
  },
  {
    target: `[data-tour="${MASTERCLASS_ONBOARDING_TARGETS.reserveMySpot}"]`,
    content: (
      <>
        <Typography variant="title3" className="mb-1">
          Join an Upcoming Masterclass
        </Typography>
        <Typography variant="smallBody" className="text-onSurface-600">
          Click ‘Reserve My Spot’ to save your seat for the live masterclass.
        </Typography>
      </>
    ),
    disableBeacon: true,
  },
  {
    target: `[data-tour="${MASTERCLASS_ONBOARDING_TARGETS.previous}"]`,
    content: (
      <>
        <Typography variant="title3" className="mb-1">
          Previous Masterclasses
        </Typography>
        <Typography variant="smallBody" className="text-onSurface-600">
          Watch all previous Masterclasses included with your Suraasa Plus
          membership.
        </Typography>
      </>
    ),
    disableBeacon: true,
  },
  {
    target: `[data-tour="${MASTERCLASS_ONBOARDING_TARGETS.certificate}"]`,
    content: (
      <>
        <Typography variant="title3" className="mb-1">
          Masterclass Certificates
        </Typography>
        <Typography variant="smallBody" className="text-onSurface-600">
          View and download your Masterclass certificates with ease.
        </Typography>
      </>
    ),
    disableBeacon: true,
  },
]
const withOutUpcomingMasterclasses: Step[] = [
  {
    target: `[data-tour="${MASTERCLASS_ONBOARDING_TARGETS.explore}"]`,
    content: (
      <>
        <Typography variant="title3" className="mb-1">
          Explore Masterclasses
        </Typography>
        <Typography variant="smallBody" className="text-onSurface-600">
          Explore 60+ expert masterclass recordings and tune in for upcoming
          ones.
        </Typography>
      </>
    ),
    disableBeacon: true,
  },
  {
    target: `[data-tour="${MASTERCLASS_ONBOARDING_TARGETS.previous}"]`,
    content: (
      <>
        <Typography variant="title3" className="mb-1">
          Previous Masterclasses
        </Typography>
        <Typography variant="smallBody" className="text-onSurface-600">
          Watch all previous Masterclasses included with your Suraasa Plus
          membership.
        </Typography>
      </>
    ),
    disableBeacon: true,
  },
  {
    target: `[data-tour="${MASTERCLASS_ONBOARDING_TARGETS.certificate}"]`,
    content: (
      <>
        <Typography variant="title3" className="mb-1">
          Masterclass Certificates
        </Typography>
        <Typography variant="smallBody" className="text-onSurface-600">
          View and download your Masterclass certificates with ease.
        </Typography>
      </>
    ),
    disableBeacon: true,
  },
]
const MasterclassOnboarding = ({
  onStart,
  onEnd,
  setActiveTab,
  hasUpcomingMasterclass,
  isLoading,
}: Props) => {
  const { isSPNYUser } = useSuraasaPlusContext()
  const { step, setStep } = useTourContext()
  const { spnyMasterclassTour, isSpnyPlatformIntroCompleted } =
    useSuraasaPlusNewYear()
  const [steps, setSteps] = useState<Step[]>([])

  const [showFinalDialog, setShowFinalDialog] = useState(false)

  const handleJoyrideFinish = () => {
    setStep("loading")
    setShowFinalDialog(true)
  }

  const handleFinalFinish = () => {
    tourManager.completed(BROWSER_STORAGE_KEYS.spnyMasterclass)
    setShowFinalDialog(false)
    onEnd()
  }

  const handleCloseOnboarding = () => {
    setStep(null)
    onEnd()
  }

  const handleContinue = () => {
    onStart()

    setStep("joyride")
  }

  // Effect 1: Set steps when data is ready
  useEffect(() => {
    if (!isSPNYUser) return

    if (!spnyMasterclassTour && isLoading) {
      setStep("loading")
      return
    }

    if (!isLoading) {
      setSteps(
        hasUpcomingMasterclass
          ? withUpcomingMasterclasses
          : withOutUpcomingMasterclasses
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUpcomingMasterclass, isLoading, spnyMasterclassTour])

  useEffect(() => {
    if (
      !spnyMasterclassTour &&
      !isLoading &&
      steps.length > 0 &&
      isSpnyPlatformIntroCompleted
    ) {
      const timeoutId = setTimeout(() => {
        setStep("joyride")
        tourManager.initiated(BROWSER_STORAGE_KEYS.spnyMasterclass)
      }, 500)

      return () => clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, isLoading, spnyMasterclassTour, isSpnyPlatformIntroCompleted])

  useEffect(() => {
    return () => {
      setStep(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!isSPNYUser || spnyMasterclassTour === SpnyTourEnum.completed) return null

  return (
    <>
      <Joyride
        steps={steps}
        // stepIndex={stepIndex}
        run={step === "joyride"}
        tooltipComponent={props => (
          <OnboardingStep
            toolTipRenderProps={props}
            switchTabsFunc={() => {
              setActiveTab(MasterclassTabs.myCertificates)
            }}
            finalStepFunc={handleJoyrideFinish}
          />
        )}
        continuous
        disableOverlayClose
        scrollOffset={120}
        scrollDuration={500}
        hideBackButton
        hideCloseButton
      />
      <ResumeMasterclassTour
        onClose={handleCloseOnboarding}
        onContinue={handleContinue}
      />
      <FinalStep
        hasUpcomingMasterclass={hasUpcomingMasterclass}
        open={showFinalDialog}
        handleFinish={handleFinalFinish}
      />
    </>
  )
}

export default MasterclassOnboarding
